<template>
  <LineChart
    ref="chart"
    :unit="unit"
    :scale="scale"
    :title="widgettitle"
    :daterange="daterange"
    :loading="loading"
  />
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { formatDate } from '@shopworx/services/util/date.service';
import LineChart from '../LineChart.vue';

export default {
  name: 'PPMDaywise',
  props: {
    title: { type: String, default: '' },
    scale: { type: Number, default: 1 },
    unit: { type: String },
    lastdays: { type: Number },
  },
  components: { LineChart },
  data() {
    return {
      widgettitle: '', daterange: '', loading: false, rawReportData: [], starttimeTs: 0,
    };
  },
  computed: {
    ...mapState('cellwiseQualityDashboard', [
      'shifts', 'machines', 'serverTime', 'dataFromJSON', 'currentDate', 'selectedCell',
    ]),
    ...mapGetters('cellwiseQualityDashboard', ['firstShift', 'activeSite']),

    cellname() {
      return this.selectedCell || this.$route.query.selectedcell || '';
    },
  },

  watch: {
    cellname: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          if (this.rawReportData.length > 0) {
            this.setOptions(this.rawReportData, this.starttimeTs);
          } else if (!this.dataFromJSON) {
            this.fetchData();
          }
        }
      },
    },
    currentDate(newVal, oldVal) {
      if (newVal && newVal !== oldVal && !this.dataFromJSON) {
        this.fetchData();
      }
    },
  },
  mounted() {
    this.widgettitle = this.title;
    if (this.dataFromJSON) this.dummydata();
    else this.fetchData();
  },
  methods: {
    ...mapActions('cellwiseQualityDashboard', [
      'allShifts', 'getReports', 'getFileData', 'getMachines',
    ]),

    async dummydata() {
      const res = await this.getFileData('jsondata/ppmdaywise.json');
      const end = new Date().getTime();
      const start = end - (86400000 * 7) - 1;
      this.setTitle(start, end - 86400000);
      const days = this.getDayList(start);
      const categories = days.map((d) => d.substring(0, 5));

      this.rawReportData = res.reportData || [];
      this.starttimeTs = start;

      if (this.$refs.chart) {
        this.$refs.chart.assignData({ categories, series: this.rawReportData });
      }
    },

    async fetchData() {
      this.loading = true;
      if (!this.shifts || this.shifts.length <= 0) await this.allShifts();

      if (!this.machines || this.machines.length === 0) {
        await this.getMachines('?sortquery=sortindex==1');
      }

      if (!this.currentDate) {
        this.loading = false;
        return;
      }

      const dateStr = String(this.currentDate);
      const year = parseInt(dateStr.slice(0, 4), 10);
      const month = parseInt(dateStr.slice(4, 6), 10) - 1;
      const day = parseInt(dateStr.slice(6, 8), 10);

      const endDateObj = new Date(year, month, day);
      endDateObj.setDate(endDateObj.getDate() - 1);

      const startDateObj = new Date(endDateObj.getTime());
      startDateObj.setDate(startDateObj.getDate() - (this.lastdays - 1));

      const startYYYYMMDD = parseInt(
        `${startDateObj.getFullYear()}${String(startDateObj.getMonth() + 1).padStart(2, '0')}${String(startDateObj.getDate()).padStart(2, '0')}`,
        10,
      );
      const endYYYYMMDD = parseInt(
        `${endDateObj.getFullYear()}${String(endDateObj.getMonth() + 1).padStart(2, '0')}${String(endDateObj.getDate()).padStart(2, '0')}`,
        10,
      );

      this.starttimeTs = startDateObj.getTime();
      const endtimeTs = new Date(endDateObj).setHours(23, 59, 59, 999);

      this.setTitle(this.starttimeTs, endtimeTs);

      let siteId = this.activeSite
       || this.$store.state.user?.activeSite || this.$store.state.auth?.user?.site_id;
      if (!siteId) {
        try {
          const swxUser = JSON.parse(localStorage.getItem('user') || '{}');
          siteId = swxUser?.site_id || swxUser?.site?.id || swxUser?.customer?.site_id;
        } catch (e) { /* ignore */ }
      }

      const payload = {
        start: startYYYYMMDD,
        end: endYYYYMMDD,
        siteid: siteId || 0,
        singledaterange: false,
        all: true,
      };

      const reportName = 'dailyanalysis';
      const result = await this.getReports({ reportName, payload });

      try {
        const parsed = typeof result === 'string' ? JSON.parse(result) : result;

        this.rawReportData = parsed.reportData || parsed || [];
        this.setOptions(this.rawReportData, this.starttimeTs);
      } catch (e) {
        this.rawReportData = [];
        this.setOptions([], this.starttimeTs);
      }
      this.loading = false;
    },

    setOptions(data, startdate) {
      // Parse potential stringified json in the raw data
      let processedData = data;
      if (typeof processedData === 'string') {
        try { processedData = JSON.parse(processedData); } catch (e) { processedData = {}; }
      }
      let safeData = [];
      if (processedData.reportData) {
        safeData = typeof processedData.reportData === 'string'
          ? JSON.parse(processedData.reportData).reportData
          : processedData.reportData;
      } else {
        safeData = Array.isArray(processedData) ? processedData : [];
      }

      let machinesInCell = [];
      if (this.cellname && this.machines) {
        machinesInCell = this.machines
          .filter((m) => m.machinecell === this.cellname
           || m.cellname === this.cellname || m.cell === this.cellname)
          .map((m) => m.machinename);
      }

      const databycellname = safeData.filter((d) => {
        if (!this.cellname) return true;
        return machinesInCell.includes(d.machinename);
      });

      const days = this.getDayList(startdate);
      const series = [{ name: 'PPM:', data: [] }];
      const categories = [];

      days.forEach((dateStr) => {
        let production = 0;
        let rejected = 0;

        const [day, month, year] = dateStr.split('-');
        const targetTime = new Date(`${year}-${month}-${day}`).setHours(0, 0, 0, 0);

        databycellname.forEach((d) => {
          if (d.date) {
            const [dDay, dMonth, dYear] = d.date.split('-');
            const dTime = new Date(`${dYear}-${dMonth.padStart(2, '0')}-${dDay.padStart(2, '0')}`).setHours(0, 0, 0, 0);

            if (targetTime === dTime) {
              const prod = d.produced || 0;
              const ppm = d.rejectionppm || 0;
              production += prod;
              rejected += (ppm * prod) / 1000000;
            }
          }
        });

        categories.push(`${day}/${month}`);
        const finalPpm = production ? Math.floor((rejected / production) * 1000000) : 0;
        series[0].data.push(finalPpm);
      });

      if (this.$refs.chart) this.$refs.chart.assignData({ categories, series });
    },

    setTitle(start, end) {
      this.widgettitle = `Last ${this.lastdays} Days PPM (NOS)`;
      this.daterange = `${formatDate(start, 'dd/MM/yyyy')} - ${formatDate(end, 'dd/MM/yyyy')}`;
    },

    getDayList(start) {
      const daylist = [];
      const date = new Date(start);
      for (let index = 0; index < this.lastdays; index += 1) {
        daylist.push(formatDate(date, 'dd-MM-yyyy'));
        date.setDate(date.getDate() + 1);
      }
      return daylist;
    },

  },
};
</script>
